import { z } from 'zod';
import { SEVERITIES } from '../types.js';
import { EXTENSION_CHANGE_KINDS, EXTENSION_RISK_LEVELS, EXTENSION_SOURCES, EXTENSION_STATUSES, STORE_STATUSES } from './types.js';
import { EXTENSION_VERDICTS } from './scan-types.js';

/**
 * Extension rules (P3, docs/design/security-roadmap-v1.md "A4") — shared contracts (server API, console editor).
 * All conditions are optional and combined with AND; at least one must be set (a rule never matches "everything"
 * by accident). Change-based conditions look at the extension's timeline within `withinDays` (default 30).
 */

/** Change kinds a rule may react to. Status changes are excluded so rules can never trigger each other. */
export const RULE_CHANGE_KINDS = EXTENSION_CHANGE_KINDS.filter((k) => k !== 'approval_changed');

export const ALERT_SEVERITIES = SEVERITIES;

/** Evidence-trust model (docs/threat-protection.md): what a matched fact rests on. */
export const EVIDENCE_LEVELS = ['device_claim', 'corroborated', 'canonical_store', 'authoritative_intel'] as const;
export type EvidenceLevel = (typeof EVIDENCE_LEVELS)[number];

const uniq = <T>(a: T[]) => [...new Set(a)];
const list = <T extends z.ZodTypeAny>(item: T, max: number) => z.array(item).min(1).max(max).transform((a) => uniq(a as Array<z.infer<T>>));

export const extensionRuleConditionsSchema = z
  .object({
    /** Scan verdict of the current version (P2). Extensions not scanned have verdict "unknown". */
    verdictIn: list(z.enum(EXTENSION_VERDICTS), EXTENSION_VERDICTS.length).optional(),
    riskLevelAtLeast: z.enum(EXTENSION_RISK_LEVELS).optional(),
    sourceIn: list(z.enum(EXTENSION_SOURCES), EXTENSION_SOURCES.length).optional(),
    storeStatusIn: list(z.enum(STORE_STATUSES), STORE_STATUSES.length).optional(),
    /** Any of these changes detected within `withinDays`. */
    changeKindsAny: list(z.enum(EXTENSION_CHANGE_KINDS).refine((k) => k !== 'approval_changed', 'status changes cannot trigger rules'), 40).optional(),
    /** A version added any of these permissions or host patterns within `withinDays` (exact names, e.g. "cookies"). */
    addedPermissionsAny: list(z.string().trim().min(1).max(200), 50).optional(),
    /** A version gained access to all websites (<all_urls>, *://*\/*) within `withinDays`. */
    addedAllSites: z.literal(true).optional(),
    /** The store publisher changed within `withinDays`. */
    publisherChanged: z.literal(true).optional(),
    /** Current review status. */
    statusIn: list(z.enum(EXTENSION_STATUSES), EXTENSION_STATUSES.length).optional(),
    withinDays: z.number().int().min(1).max(365).default(30),
  })
  .strict()
  .refine((c) => Object.entries(c).some(([k, v]) => k !== 'withinDays' && v !== undefined), { message: 'set at least one condition' });
export type ExtensionRuleConditions = z.infer<typeof extensionRuleConditionsSchema>;
/** Conditions as sent to the API (`withinDays` optional, lists not yet de-duplicated). */
export type ExtensionRuleConditionsInput = z.input<typeof extensionRuleConditionsSchema>;

export const extensionRuleActionSchema = z.discriminatedUnion('type', [
  /** Status → blocked: Edge policy artifacts + protection bundle `extensions.disableIds`. */
  z.object({ type: z.literal('block') }).strict(),
  z.object({ type: z.literal('set_status'), status: z.enum(['review', 'restricted']) }).strict(),
  z.object({ type: z.literal('alert'), severity: z.enum(ALERT_SEVERITIES) }).strict(),
]);
export type ExtensionRuleAction = z.infer<typeof extensionRuleActionSchema>;
